// Pure helpers for the post-travel survey. No Vue, no store, no HTTP.
//
// What a booking is asked is decided by the API (POST /api/pt-survey/definition);
// this file only knows how to walk and validate whatever definition comes back.
// It is deliberately framework-free so the ViewTrail mobile app can port it
// line-for-line alongside the same API contract.
//
// Definition shape (abridged):
//   {
//     overview: { paragraphs: [{ text, highlight? }] },
//     sections: [{ key, section, title, codes, variant, questions: [...] }],
//     closing:  { id, text, optional }
//   }
//
// A question: { id, type: radio|yesno|matrix|text, text, options?, items?,
//               reveal?: { label, triggers[] }, optional?,
//               followUp?: { showWhen[], question } }

// The API only needs the raw itinerary lines. Which jsontypes and suppliers count
// is a BE rule, so nothing is filtered here.
const itineraryItemsFor = booking => {
  const days =
    booking && booking.booking_itinerary && Array.isArray(booking.booking_itinerary.itinerary)
      ? booking.booking_itinerary.itinerary
      : []
  return days.flatMap(day => (day && Array.isArray(day.data) ? day.data : []))
    .filter(Boolean)
    .map(item => ({ jsontype: item.jsontype, suppliercode: item.suppliercode || item.supplier }))
}

// Answers and reveals live in flat maps keyed by section + question, so the same
// question id can be answered separately for each tour.
const answerKey = (section, questionId) => `${section.key}_${questionId}`

const revealShown = (question, answer) =>
  !!(question && question.reveal && question.type !== 'matrix' && question.reveal.triggers.includes(answer))

const followUpOf = question => (question && question.followUp && question.followUp.question) || null

const followUpShown = (question, answer) =>
  !!(followUpOf(question) && (question.followUp.showWhen || []).includes(answer))

// Radio buttons are mandatory, free text never is. A follow-up counts as part of
// its parent's page, so it must be answered before moving on whenever it is shown.
const isQuestionComplete = (question, answer, followUpAnswer) => {
  if (!question) return false
  if (question.optional) return true
  if (question.type === 'matrix') {
    const rows = Array.isArray(answer) ? answer : []
    return (question.items || []).every(item => {
      const row = rows.find(a => a.id === item.id)
      return !!(row && row.answer)
    })
  }
  if (!answer) return false
  return !followUpShown(question, answer) || !!followUpAnswer
}

const totalQuestionCount = sections => sections.reduce((total, section) => total + section.questions.length, 0)

// Stored keyed by section instance key (the supplier code for a tour, the section
// key otherwise), so Touring Hub records keep the shape reporting already reads.
// Every question asked is written, null when skipped, so reports can tell "not
// asked" from "asked but skipped". A detail is only kept while its reveal is shown.
const buildSurveyAnswers = (sections, answers, reveals) => {
  const result = {}
  sections.forEach(section => {
    const out = {}
    section.questions.forEach(q => {
      const key = answerKey(section, q.id)
      const answer = answers[key] === undefined ? null : answers[key]
      out[q.id] = answer
      if (revealShown(q, answer) && reveals[key]) out[`${q.id}_detail`] = reveals[key]

      const followUp = followUpOf(q)
      if (followUp && followUpShown(q, answer)) {
        const fKey = answerKey(section, followUp.id)
        const fAnswer = answers[fKey] === undefined ? null : answers[fKey]
        out[followUp.id] = fAnswer
        if (revealShown(followUp, fAnswer) && reveals[fKey]) out[`${followUp.id}_detail`] = reveals[fKey]
      }
    })
    result[section.key] = out
  })
  return result
}

// Echoed back with the submission so the email and CSV export know how to label
// and split each block of answers without re-deriving it.
const surveySectionsFor = sections =>
  sections.map(({ key, section, title, codes, variant }) => ({ key, section, title, codes, variant }))

export {
  itineraryItemsFor,
  answerKey,
  revealShown,
  followUpOf,
  followUpShown,
  isQuestionComplete,
  totalQuestionCount,
  buildSurveyAnswers,
  surveySectionsFor,
}
